/**
 * Promise-based semaphore for limiting concurrent operations
 * Prevents overwhelming the server/browser with too many parallel requests
 */

export class ConcurrencyLimiter {
  constructor (limit) {
    this.limit = limit
    this.active = 0
    this.queue = []
  }

  async acquire () {
    if (this.active < this.limit) {
      this.active++
      return Promise.resolve()
    }

    // Wait in queue
    return new Promise(resolve => {
      this.queue.push(resolve)
    })
  }

  release () {
    this.active--

    // Process next in queue
    if (this.queue.length > 0) {
      const resolve = this.queue.shift()
      this.active++
      resolve()
    }
  }

  async run (fn) {
    await this.acquire()
    try {
      return await fn()
    } finally {
      this.release()
    }
  }
}

/**
 * Execute an array of async tasks with a concurrency limit
 * @param {Array} tasks - Array of task functions that return Promises
 * @param {number} concurrency - Max number of concurrent tasks
 * @returns {Promise<Array>} - Results in same order as tasks
 */
export async function executeWithConcurrency (tasks, concurrency = 4) {
  const limiter = new ConcurrencyLimiter(concurrency)

  // Execute all tasks with concurrency limit
  // Results maintain order via Promise.all
  return Promise.all(
    tasks.map(task => limiter.run(task))
  )
}
